import type { FamilyDescriptor, FamilyUnavailable } from '@beyond-js/ui/react';
import { type Client, Refusal } from '@accounts/transport';
import type { Providers } from './types';

/**
 * What the family bar shows: the `beyond-family/1` descriptor Projects answers, read through this
 * service's relay (`GET /v1/family`), which forwards the person's platform session so the browser never
 * holds it.
 *
 * The descriptor is navigation with context and grants nothing, and the bar must never break a page.
 * So `read` never throws: an absent or failing Projects, a relay that did not answer within the
 * client's bound, a refusal or an answer that is not a descriptor are all `{unavailable: true}`, which
 * the bar shows with the names this interface knows itself. A read the page's session withheld (a
 * session that ended, which its dialog answers) returns the last descriptor read for that organization,
 * so the bar stays as it was.
 */
export class Family {
	static PROTOCOL = 'beyond-family/1';

	#client: Client;
	#providers: () => Promise<Providers>;
	#projects: Promise<string | undefined> | undefined;
	#last = new Map<string, FamilyDescriptor>();

	/**
	 * @param providers Reads this installation's products, where the Projects interface is named
	 */
	constructor(client: Client, providers: () => Promise<Providers>) {
		this.#client = client;
		this.#providers = providers;
	}

	/**
	 * @param organization The organization in view, when a screen shows one
	 */
	async read(organization?: string): Promise<FamilyDescriptor | FamilyUnavailable> {
		const query = organization ? `?organization=${encodeURIComponent(organization)}` : '';
		try {
			const answer = await this.#client.get<FamilyDescriptor | { unavailable: true; reason?: string }>(`/v1/family${query}`);
			if (answer?.unavailable === true || (answer as FamilyDescriptor)?.protocol !== Family.PROTOCOL) return { unavailable: true };
			this.#last.set(query, answer as FamilyDescriptor);
			return answer as FamilyDescriptor;
		} catch (error) {
			const kept = this.#last.get(query);
			return Refusal.is(error) && error.quiet && kept ? kept : { unavailable: true };
		}
	}

	/**
	 * Where the wordmark leads while there is no descriptor: the Projects interface this installation
	 * serves, as its product list names it, or this interface's own overview when it names none. Read
	 * once per page load, and never a failure.
	 */
	async home(): Promise<string> {
		return (await this.#origin().catch(() => undefined)) ?? '/';
	}

	/**
	 * The Projects interface this installation's product list names, read once per page load once it
	 * answers. A list that could not be read rejects and is not kept, so the next call reads it again.
	 */
	#origin(): Promise<string | undefined> {
		if (this.#projects) return this.#projects;
		const pending = this.#providers().then(providers => Family.web(providers.products.find(product => product.id === 'projects')?.origins[0]));
		this.#projects = pending;
		pending.catch(() => {
			if (this.#projects === pending) this.#projects = undefined;
		});
		return pending;
	}

	/** An absolute web address as given, or nothing: a link this interface draws is never another scheme */
	static web(value: unknown): string | undefined {
		if (typeof value !== 'string') return undefined;
		try {
			return ['http:', 'https:'].includes(new URL(value).protocol) ? value : undefined;
		} catch {
			return undefined;
		}
	}
}
